import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Wallet, ExternalLink, RefreshCw, TrendingUp, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

export default function BalancePage() {
  const [balance, setBalance] = useState({
    trx: 0,
    usdt: 0,
    loading: true
  });
  const [concentratorAddress] = useState('TYmH3UtfA5WgTyqLnZTSPsHcqFqKDsuk3C');

  useEffect(() => {
    fetchBalance();
    const interval = setInterval(fetchBalance, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchBalance = async () => {
    try {
      // Simular consulta de balance
      // En producción, esto debería consultar un endpoint del backend
      setBalance({
        trx: 0,
        usdt: 0,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching balance:', error);
      toast.error('Error al consultar balance');
      setBalance(prev => ({ ...prev, loading: false }));
    }
  };

  const openInExplorer = () => {
    window.open(`https://nile.tronscan.org/#/address/${concentratorAddress}`, '_blank');
  };

  if (balance.loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-teal))] mx-auto"></div>
          <p className="mt-4 text-[hsl(var(--muted-foreground))]">Cargando balance...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="balance-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--brand-ink))]" style={{ fontFamily: 'var(--font-heading)' }}>
              Balance
            </h1>
            <p className="text-[hsl(var(--muted-foreground))] mt-1">
              Monitorea el balance de tu wallet concentradora
            </p>
          </div>
          <Button onClick={fetchBalance} variant="outline" size="sm" data-testid="refresh-balance-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Wallet Address */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">Wallet Concentradora</p>
              <code className="text-sm bg-[hsl(var(--accent))] px-3 py-1.5 rounded">
                {concentratorAddress}
              </code>
            </div>
            <Button onClick={openInExplorer} variant="outline" size="sm" data-testid="view-explorer-btn">
              <ExternalLink className="w-4 h-4 mr-2" />
              Ver en Explorer
            </Button>
          </div>
        </Card>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* TRX Balance */}
          <Card className="p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-4 rounded-lg bg-[hsl(var(--brand-teal))]/10">
                <Wallet className="w-8 h-8 text-[hsl(var(--brand-teal))]" />
              </div>
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Balance TRX</p>
                <p className="text-3xl font-bold text-[hsl(var(--brand-ink))]" data-testid="trx-balance">
                  {balance.trx.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              {balance.trx >= 100 ? (
                <>
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">Suficiente para transacciones</span>
                </>
              ) : balance.trx > 0 ? (
                <>
                  <TrendingUp className="w-4 h-4 text-yellow-600" />
                  <span className="text-yellow-600">Balance bajo, recarga pronto</span>
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4 text-red-600" />
                  <span className="text-red-600">Sin fondos - Recarga necesaria</span>
                </>
              )}
            </div>
            {balance.trx === 0 && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💧 Necesitas TRX del faucet para procesar pagos
                </p>
                <a
                  href="https://nileex.io/join/getJoinPage"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline mt-1 inline-block"
                >
                  Obtener TRX testnet →
                </a>
              </div>
            )}
          </Card>

          {/* USDT Balance */}
          <Card className="p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-4 rounded-lg bg-green-50">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Balance USDT</p>
                <p className="text-3xl font-bold text-[hsl(var(--brand-ink))]" data-testid="usdt-balance">
                  {balance.usdt.toLocaleString()}
                </p>
              </div>
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              USDT acumulado de pagos procesados
            </p>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="p-6">
          <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-4">Información Importante</h3>
          <div className="space-y-3 text-sm text-[hsl(var(--muted-foreground))]">
            <div className="flex gap-2">
              <span className="font-medium text-[hsl(var(--brand-ink))]">TRX:</span>
              <span>Necesario para pagar fees de transacciones. Cada forwarding consume ~50 TRX.</span>
            </div>
            <div className="flex gap-2">
              <span className="font-medium text-[hsl(var(--brand-ink))]">USDT:</span>
              <span>Se acumula automáticamente cuando los clientes realizan pagos.</span>
            </div>
            <div className="flex gap-2">
              <span className="font-medium text-[hsl(var(--brand-ink))]">Red:</span>
              <span>TRON Testnet (Nile) - Los fondos no tienen valor real.</span>
            </div>
          </div>
        </Card>

        {/* Transaction History Placeholder */}
        <Card className="p-6">
          <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-4">Historial de Transacciones</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-8">
            Las transacciones aparecerán aquí cuando proceses pagos
          </p>
        </Card>
      </div>
    </AdminLayout>
  );
}
